import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from "react"
import { ConfirmDialog } from "@/components/ui/confirm-dialog"

interface ConfirmOptions {
  title: string
  description?: string
  confirmLabel?: string
  cancelLabel?: string
  confirmVariant?: "primary" | "danger"
  /** See ConfirmDialogProps.requireText — gate the confirm button on typing this. */
  requireText?: string
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>

const ConfirmContext = createContext<ConfirmFn | null>(null)

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const [opts, setOpts] = useState<ConfirmOptions | null>(null)
  const resolverRef = useRef<((value: boolean) => void) | null>(null)

  const confirm = useCallback<ConfirmFn>((next) => {
    setOpts(next)
    setOpen(true)
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve
    })
  }, [])

  function settle(value: boolean) {
    setOpen(false)
    resolverRef.current?.(value)
    resolverRef.current = null
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmDialog
        open={open}
        title={opts?.title ?? ""}
        description={opts?.description}
        confirmLabel={opts?.confirmLabel}
        cancelLabel={opts?.cancelLabel}
        confirmVariant={opts?.confirmVariant}
        requireText={opts?.requireText}
        onCancel={() => settle(false)}
        onConfirm={() => settle(true)}
      />
    </ConfirmContext.Provider>
  )
}

/** Imperative confirm dialog. Returns a promise that resolves to `true` if
 * the user confirms, `false` otherwise. Falls back to `window.confirm` if
 * the provider hasn't been mounted (so it's safe in tests). */
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext)
  return useCallback(
    (opts: ConfirmOptions) => {
      if (ctx) return ctx(opts)
      return Promise.resolve(window.confirm(`${opts.title}\n${opts.description ?? ""}`.trim()))
    },
    [ctx],
  )
}
